import React, { useState } from 'react';

const Toolbar = ({
  editorMode,
  setEditorMode,
  addColumn,
  addRow,
  addExternalSource,
  exportAsImage,
  saveGrid,
  loadGrid,
  loadEmptyTemplate,
  zoomLevel,
  onZoomIn,
  onZoomOut,
  onZoomReset,
  onShowTooltip,
  onHideTooltip,
  validateGrid,
  validationErrors,
  showValidationErrors,
  setShowValidationErrors,
  automaticValidation,
  setAutomaticValidation,
}) => {
  // États pour les formulaires
  const [newColumnTitle, setNewColumnTitle] = useState('');
  const [newRowTitle, setNewRowTitle] = useState('');
  const [newRowHorizon, setNewRowHorizon] = useState('H');
  const [newSourceTitle, setNewSourceTitle] = useState('');
  const [newSourcePosition, setNewSourcePosition] = useState('top');
  const [columnInsertPosition, setColumnInsertPosition] = useState('end');

  // États pour les modales
  const [showColumnModal, setShowColumnModal] = useState(false);
  const [showRowModal, setShowRowModal] = useState(false);
  const [showSourceModal, setShowSourceModal] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [showZoomControls, setShowZoomControls] = useState(false);
  
  // Gérer l'ajout d'une colonne
  const handleAddColumn = () => {
  if (newColumnTitle.trim()) {
    addColumn(newColumnTitle, columnInsertPosition);
    setNewColumnTitle('');
    setColumnInsertPosition('end');
    setShowColumnModal(false);
  }
  };

  
  // Gérer l'ajout d'une ligne
  const handleAddRow = () => {
    if (newRowTitle.trim() && newRowHorizon.trim()) {
      addRow(newRowTitle, newRowHorizon);
      setNewRowTitle('');
      setNewRowHorizon('H');
      setShowRowModal(false);
    }
  };
  
  // Gérer l'ajout d'une source externe
  const handleAddSource = () => {
    if (newSourceTitle.trim()) {
      addExternalSource(newSourceTitle, newSourcePosition);
      setNewSourceTitle('');
      setShowSourceModal(false);
    }
  };
  
  // Annuler l'ajout
  const handleCancel = (modalSetter) => {
    modalSetter(false);
  };
  
  // Gérer les touches dans les champs de texte
  const handleKeyDown = (e, submitHandler) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      submitHandler();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      
      if (showColumnModal) handleCancel(setShowColumnModal);
      if (showRowModal) handleCancel(setShowRowModal);
      if (showSourceModal) handleCancel(setShowSourceModal);
    }
  };

  // Ajouter une nouvelle section dans la toolbar pour la validation GRAI
  const validationSection = (
    <div className="toolbar-section">
      <button 
        className={`toolbar-button ${validationErrors.length > 0 ? 'has-errors' : ''}`}
        onClick={() => {
          validateGrid();
          setShowValidationErrors(true);
        }}
        title="Vérifier les règles GRAI"
        onMouseEnter={(e) => onShowTooltip(e, "Valider la conformité aux règles GRAI")}
        onMouseLeave={onHideTooltip}
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
          <polyline points="22 4 12 14.01 9 11.01"></polyline>
        </svg>
        Validation
        {validationErrors.length > 0 && (
          <span className="validation-badge">{validationErrors.length}</span>
        )}
      </button>
      
      <div className="toolbar-toggle">
        <input 
          type="checkbox" 
          id="auto-validation" 
          checked={automaticValidation} 
          onChange={() => setAutomaticValidation(!automaticValidation)}
        />
        <label 
          htmlFor="auto-validation"
          title="Activer/désactiver la validation automatique"
          onMouseEnter={(e) => onShowTooltip(e, "Validation en temps réel")}
          onMouseLeave={onHideTooltip}
        >
          Validation auto
        </label>
      </div>
    </div>
  );

  return (
    <div className="grai-toolbar">
      {/* Section Mode d'édition */}
      <div className="toolbar-section">
        {/* Bouton Éditer */}
        <button
          className={`toolbar-button ${editorMode === 'cell' ? 'active' : ''}`}
          onClick={() => setEditorMode('cell')}
          title="Mode Édition"
          onMouseEnter={(e) => onShowTooltip(e, "Mode édition des cellules")}
          onMouseLeave={onHideTooltip}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M17 3a2.85 2.85 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"></path>
          </svg>
          Éditer
        </button>

        {/* Groupe Relations */}
        <div className="toolbar-group">
          <h4>Relations</h4>
          <div className="relation-buttons">
            <button
              className={`toolbar-button ${editorMode === 'arrow-info' ? 'active' : ''}`}
              onClick={() => setEditorMode('arrow-info')}
              title="Flèche d'information"
              onMouseEnter={(e) => onShowTooltip(e, "Créer une flèche d'information (rouge, horizontale)")}
              onMouseLeave={onHideTooltip}
            >
              ➜ Flèche d’information
            </button>

            <button
              className={`toolbar-button ${editorMode === 'arrow-decision' ? 'active' : ''}`}
              onClick={() => setEditorMode('arrow-decision')}
              title="Flèche de décision"
              onMouseEnter={(e) => onShowTooltip(e, "Créer une flèche de décision (bleue, verticale)")}
              onMouseLeave={onHideTooltip}
            >
              ⬇ Flèche de décision
            </button>
          </div>
        </div>
      </div>
   
      <div className="toolbar-divider"></div>
      
      {/* Section Structure de la grille */}
      <div className="toolbar-section">
        {!showColumnModal ? (
  <button 
    className="toolbar-button"
    onClick={() => setShowColumnModal(true)}
    title="Ajouter une fonction"
    onMouseEnter={(e) => onShowTooltip(e, "Ajouter une nouvelle colonne")}
    onMouseLeave={onHideTooltip}
  >
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="5" x2="12" y2="19"></line>
      <line x1="5" y1="12" x2="19" y2="12"></line>
    </svg>
    Colonne
  </button>
) : (
  <div className="toolbar-modal">
    <input
      type="text"
      value={newColumnTitle}
      onChange={(e) => setNewColumnTitle(e.target.value)}
      onKeyDown={(e) => handleKeyDown(e, handleAddColumn)}
      className="toolbar-input"
      placeholder="Nom de la fonction"
      autoFocus
    />

    {/* ✅ Sélecteur de position */}
    <select
      value={columnInsertPosition}
      onChange={(e) => setColumnInsertPosition(e.target.value)}
      className="toolbar-input"
      title="Choisir la position d'insertion"
    >
      <option value="end">À la fin</option>
      <option value="beforeProduct">Avant “Gérer produits”</option>
      <option value="afterResource">Après “Gérer ressources”</option>
    </select>

    <button 
      className="toolbar-button confirm"
      onClick={handleAddColumn}
      title="Confirmer"
    >
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12"></polyline>
      </svg>
    </button>
    <button 
      className="toolbar-button cancel"
      onClick={() => handleCancel(setShowColumnModal)}
      title="Annuler"
    >
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="18" y1="6" x2="6" y2="18"></line>
        <line x1="6" y1="6" x2="18" y2="18"></line>
      </svg>
    </button>
  </div>
)}

        
        {!showRowModal ? (
          <button 
            className="toolbar-button"
            onClick={() => setShowRowModal(true)}
            title="Ajouter un horizon temporel"
            onMouseEnter={(e) => onShowTooltip(e, "Ajouter un nouvel horizon")}
            onMouseLeave={onHideTooltip}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="5" x2="12" y2="19"></line>
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
            Ligne
          </button>
        ) : (
          <div className="toolbar-modal">
            <div className="modal-form">
              <input
                type="text"
                value={newRowTitle}
                onChange={(e) => setNewRowTitle(e.target.value)}
                onKeyDown={(e) => handleKeyDown(e, handleAddRow)}
                className="toolbar-input"
                placeholder="Description (ex: 1 an)"
                autoFocus
              />
              <input
                type="text"
                value={newRowHorizon}
                onChange={(e) => setNewRowHorizon(e.target.value)}
                onKeyDown={(e) => handleKeyDown(e, handleAddRow)}
                className="toolbar-input horizon-input"
                placeholder="Code (ex: H1)"
              />
            </div>
            <button 
              className="toolbar-button confirm"
              onClick={handleAddRow}
              title="Confirmer"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12"></polyline>
              </svg>
            </button>
            <button 
              className="toolbar-button cancel"
              onClick={() => handleCancel(setShowRowModal)}
              title="Annuler"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>
        )}
      </div>
      
      <div className="toolbar-divider"></div>
      
      {/* Section Sources externes */}
      <div className="toolbar-section">
        {!showSourceModal ? (
          <button 
            className="toolbar-button"
            onClick={() => setShowSourceModal(true)}
            title="Ajouter une source d'information"
            onMouseEnter={(e) => onShowTooltip(e, "Ajouter une source externe")}
            onMouseLeave={onHideTooltip}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 5v14"></path>
              <path d="M5 12h14"></path>
            </svg>
            Source
          </button>
        ) : (
          <div className="toolbar-modal">
            <input
              type="text"
              value={newSourceTitle}
              onChange={(e) => setNewSourceTitle(e.target.value)}
              onKeyDown={(e) => handleKeyDown(e, handleAddSource)}
              className="toolbar-input"
              placeholder="Nom de la source"
              autoFocus
            />
            <select 
              value={newSourcePosition}
              onChange={(e) => setNewSourcePosition(e.target.value)}
              className="toolbar-input"
            >
              <option value="top">Haut</option>
              <option value="right">Droite</option>
              <option value="bottom">Bas</option>
              <option value="left">Gauche</option>
            </select>
            <button 
              className="toolbar-button confirm"
              onClick={handleAddSource}
              title="Confirmer"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12"></polyline>
              </svg>
            </button>
            <button 
              className="toolbar-button cancel"
              onClick={() => handleCancel(setShowSourceModal)}
              title="Annuler"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>
        )}
      </div>
      
      <div className="toolbar-divider"></div>
      
      {/* Section Zoom */}
      <div className="toolbar-section">
        {!showZoomControls ? (
          <button 
            className="toolbar-button"
            onClick={() => setShowZoomControls(true)}
            title="Contrôles de zoom"
            onMouseEnter={(e) => onShowTooltip(e, "Ajuster le niveau de zoom")}
            onMouseLeave={onHideTooltip}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8"></circle>
              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
              <line x1="11" y1="8" x2="11" y2="14"></line>
              <line x1="8" y1="11" x2="14" y2="11"></line>
            </svg>
            Zoom {zoomLevel}%
          </button>
        ) : (
          <div className="toolbar-modal">
            <button 
              className="toolbar-button"
              onClick={onZoomOut}
              disabled={zoomLevel <= 50}
              title="Zoom arrière"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8"></circle>
                <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                <line x1="8" y1="11" x2="14" y2="11"></line>
              </svg>
            </button>
            <div className="zoom-level">{zoomLevel}%</div>
            <button 
              className="toolbar-button"
              onClick={onZoomReset}
              title="Réinitialiser le zoom"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"></path>
                <path d="M3 3v5h5"></path>
              </svg>
            </button>
            <button 
              className="toolbar-button"
              onClick={onZoomIn}
              disabled={zoomLevel >= 200}
              title="Zoom avant"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8"></circle>
                <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                <line x1="11" y1="8" x2="11" y2="14"></line>
                <line x1="8" y1="11" x2="14" y2="11"></line>
              </svg>
            </button>
            <button 
              className="toolbar-button cancel"
              onClick={() => setShowZoomControls(false)}
              title="Fermer"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>
        )}
      </div>
      
      <div className="toolbar-divider"></div>
      
      {/* Section Validation GRAI */}
      {validationSection}
      
      <div className="toolbar-divider"></div>
      
      {/* Section Sauvegarde et Export */}
      <div className="toolbar-section">
        <button 
          className="toolbar-button save"
          onClick={saveGrid}
          title="Sauvegarder la grille"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
            <polyline points="17 21 17 13 7 13 7 21"></polyline>
            <polyline points="7 3 7 8 15 8"></polyline>
          </svg>
          Sauvegarder
        </button>
        <button 
          className="toolbar-button"
          onClick={loadGrid}
          title="Charger une grille sauvegardée"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
            <polyline points="7 10 12 15 17 10"></polyline>
            <line x1="12" y1="15" x2="12" y2="3"></line>
          </svg>
          Charger
        </button>
        <button 
          className="toolbar-button export"
          onClick={exportAsImage}
          title="Exporter en image"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
            <circle cx="8.5" cy="8.5" r="1.5"></circle>
            <polyline points="21 15 16 10 5 21"></polyline>
          </svg>
          Exporter
        </button>
      </div>
      
      <div className="toolbar-divider"></div>
      
      {/* Section Réinitialisation et Aide */}
      <div className="toolbar-section">
        <button 
          className="toolbar-button"
          onClick={loadEmptyTemplate}
          title="Nouveau modèle vide"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 6h18"></path>
            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
          </svg>
          Nouveau
        </button>
        <button 
          className="toolbar-button"
          onClick={() => setShowHelpModal(!showHelpModal)}
          title="Aide sur l'outil GRAI"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"></circle>
            <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
            <line x1="12" y1="17" x2="12.01" y2="17"></line>
          </svg>
          Aide
        </button>
      </div>
      
      {/* Modal d'aide */}
      {showHelpModal && (
        <div className="grai-help">
          <h3>Guide de l'éditeur GRAI</h3>
          <p>L'éditeur GRAI vous permet de créer des modèles décisionnels selon la méthode GRAI. Voici comment l'utiliser :</p>
          
          <ul>
            <li><strong>Éditer les cellules</strong> : Cliquez sur une cellule pour éditer son contenu</li>
            <li><strong>Ajouter des flèches</strong> : Utilisez le mode "Relations" puis cliquez sur une cellule de départ puis sur une cellule d'arrivée</li>
            <li><strong>Types de flèches</strong> : Flux de décision (bleu) ou Flux d'information (rouge)</li>
            <li><strong>Sources externes</strong> : Informations provenant de l'extérieur du système</li>
            <li><strong>Navigation</strong> : Utilisez la molette de souris (avec Ctrl) pour zoomer, ou cliquez et faites glisser avec le bouton du milieu (ou Alt+clic gauche) pour déplacer la grille</li>
          </ul>
          
          <h4>Règles GRAI appliquées</h4>
          <ul>
            <li><strong>CMG1</strong> : Un cadre de décision ne peut être reçu par ce même centre</li>
            <li><strong>CMG2/4</strong> : Un cadre de décision ne peut être émis d'une pseudo-fonction à une autre</li>
            <li><strong>CMG3</strong> : La fonction 'informations externes' est placée exclusivement en colonne 1</li>
            <li><strong>CMG5</strong> : La fonction 'informations internes' est placée exclusivement en dernière colonne</li>
            <li><strong>CMG6/7/8</strong> : Les fonctions 'gérer les produits', 'gérer les ressources', et 'planifier' sont obligatoires</li>
            <li><strong>CMG9</strong> : Les périodes des niveaux décisionnels doivent être classées par ordre décroissant</li>
            <li><strong>CMG10</strong> : Les niveaux décisionnels ne peuvent être moins de trois dans une grille GRAI</li>
          </ul>
          
          <p>Pour plus d'informations sur la méthode GRAI, consultez la documentation technique.</p>
          
          <button 
            className="toolbar-button"
            onClick={() => setShowHelpModal(false)}
          >
            Fermer
          </button>
        </div>
      )}
    </div>
  );
};

export default Toolbar; 